import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Bell, Reply, Send, Loader2, BookOpen, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "");

function ReportCard({ r, onReply }) {
  return (
    <Card className="p-4 border-slate-200 flex items-start gap-3" data-testid={`report-${r.id}`}>
      <div className="h-9 w-9 rounded-lg bg-orange-50 text-orange-600 grid place-items-center shrink-0"><Bell size={17} /></div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900" data-testid="report-participant">{r.participant || "Partecipante"}</span>
            <Badge variant="outline" className={r.status === "risposta" ? "border-emerald-200 text-emerald-700 bg-emerald-50" : "border-orange-200 text-orange-700 bg-orange-50"} data-testid="report-status">
              {r.status === "risposta" ? "Risposta inviata" : "Aperta"}
            </Badge>
          </div>
          <span className="text-xs text-slate-400 font-mono">{new Date(r.at).toLocaleString("it-IT")}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
          <span className="flex items-center gap-1" data-testid="report-course">
            <BookOpen size={13} />
            {r.course ? <Link to={`/corsi/${r.course.id}`} className="hover:underline">{r.course.code} – {r.course.title}</Link> : "Corso non disponibile"}
          </span>
          <span className="flex items-center gap-1" data-testid="report-lesson">
            <CalendarDays size={13} />
            {r.lesson ? `Lezione ${r.lesson.number}${r.lesson.title ? ` – ${r.lesson.title}` : ""}${r.lesson.date ? ` (${fmtDate(r.lesson.date)})` : ""}` : "Nessuna lezione specificata"}
          </span>
        </div>
        <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.message}</p>
        {r.replies.map((x) => (
          <div key={x.id} className="mt-2 ml-2 pl-3 border-l-2 border-emerald-300 text-sm" data-testid="report-reply">
            <div className="text-xs text-slate-400">Risposta di {x.by} · {new Date(x.at).toLocaleString("it-IT")}</div>
            <p className="text-slate-700 whitespace-pre-wrap">{x.message}</p>
          </div>
        ))}
        <Button size="sm" variant="outline" className="mt-3" onClick={() => onReply(r)} data-testid={`reply-btn-${r.id}`}>
          <Reply size={14} className="mr-1" /> Rispondi
        </Button>
      </div>
    </Card>
  );
}

function ReplyDialog({ report, onClose, onSent }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { setText(""); }, [report]);
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/anomaly-reports/${report.id}/reply`, { message: text });
      toast.success(`Risposta inviata a ${report.participant_email}`);
      onSent();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Rispondi a {report?.participant}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">La risposta sarà inviata via e-mail a <b>{report?.participant_email || "—"}</b>, con il riferimento al corso, alla lezione e alla segnalazione.</p>
        <div className="text-sm bg-slate-50 rounded-md p-3 text-slate-600 whitespace-pre-wrap">{report?.message}</div>
        <Textarea rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi la risposta…" data-testid="reply-input" />
        <DialogFooter>
          <Button onClick={send} disabled={!text.trim() || sending} className="bg-[#0B192C] hover:bg-[#07111E]" data-testid="reply-send-btn">
            {sending ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />} Invia risposta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [replying, setReplying] = useState(null);
  const load = () => api.get("/anomaly-reports").then((r) => setReports(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Segnalazioni Partecipanti" subtitle="Anomalie segnalate dai partecipanti tramite il link personale." />
      <div className="space-y-3">
        {reports.map((r) => <ReportCard key={r.id} r={r} onReply={setReplying} />)}
        {reports.length === 0 && <p className="text-slate-500">Nessuna segnalazione.</p>}
      </div>
      <ReplyDialog report={replying} onClose={() => setReplying(null)} onSent={() => { setReplying(null); load(); }} />
    </div>
  );
}
